
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { requireAuth, cleanupAuthState } from "@/utils/authUtils";

/**
 * Hook to handle admin authentication checks with improved security
 */
export const useRequestAuth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  /**
   * Enhanced authentication check that combines base auth validation
   * with admin-specific permission checks
   */
  const checkAuth = useCallback(async () => {
    try {
      // First use the shared requireAuth utility to verify basic authentication
      const isAuthenticated = await requireAuth(navigate);
      if (!isAuthenticated) return false;
      
      // If basic auth passes, check admin-specific permissions
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // This is a fallback, should rarely happen since requireAuth already checks this
        cleanupAuthState(); // Clean up any stale auth state
        navigate("/admin");
        toast({
          title: "Åtkomst nekad",
          description: "Du måste vara inloggad för att se denna sida",
          variant: "destructive",
        });
        return false;
      }
      
      // Check if user has super_admin role
      const { data: hasSuperAdminRole, error: roleError } = await supabase.rpc(
        'has_role',
        { 
          user_id: session.user.id,
          role: 'super_admin'
        }
      );
      
      if (roleError || !hasSuperAdminRole) {
        navigate("/admin");
        toast({
          title: "Åtkomst nekad",
          description: "Du har inte behörighet att se denna sida",
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Auth check failed:", error);
      cleanupAuthState(); // Clean up any stale auth state
      navigate("/admin");
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte verifiera din behörighet",
        variant: "destructive",
      });
      return false;
    }
  }, [navigate, toast]);

  return { checkAuth };
};
